import { Component, OnInit, ChangeDetectorRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { HttpClient } from '@angular/common/http';
import { SharedService } from '../../services/shared.service';
import { LocalDatabaseService } from '../../services/local-database.service';
import { AccountingService } from '../../../accounting/services/accounting.service';
import { CalendarService } from '../../../calendar/services/calendar.service';
import { firstValueFrom } from 'rxjs';

interface CollectionStatus {
  label: string;
  key: string;
  local: number | null;
  remote: number | null;
  status: 'loading' | 'local-done' | 'ok' | 'mismatch' | 'error' | 'syncing';
}

// Maps each collection key to the Dexie table query
type LocalCountFn = (cid: string) => Promise<number>;

@Component({
  selector: 'app-sync-check-modal',
  templateUrl: './sync-check-modal.component.html',
  styleUrls: ['./sync-check-modal.component.scss'],
  standalone: true,
  imports: [CommonModule],
})
export class SyncCheckModalComponent implements OnInit {
  companyId: string = '';
  isLoading = false;
  isSyncing = false;
  hasError = false;
  lastChecked: Date | null = null;

  collections: CollectionStatus[] = [
    { label: 'Bound Books',       key: 'boundBooks',       local: null, remote: null, status: 'loading' },
    { label: 'Firearms',          key: 'firearms',         local: null, remote: null, status: 'loading' },
    { label: 'FFLs',              key: 'ffls',             local: null, remote: null, status: 'loading' },
    { label: 'Vendors',           key: 'vendors',          local: null, remote: null, status: 'loading' },
    { label: 'Customers',         key: 'customers',        local: null, remote: null, status: 'loading' },
    { label: 'Products/Services', key: 'productsServices', local: null, remote: null, status: 'loading' },
    { label: 'Sales Receipts',    key: 'salesReceipts',    local: null, remote: null, status: 'loading' },
    { label: 'Expense Records',   key: 'expenseRecords',   local: null, remote: null, status: 'loading' },
    { label: 'Form 4473s',        key: 'form4473s',        local: null, remote: null, status: 'loading' },
    { label: 'Reserved Firearms', key: 'reservedFirearms', local: null, remote: null, status: 'loading' },
    { label: 'Reserved 4473s',    key: 'reserved4473s',    local: null, remote: null, status: 'loading' },
    { label: 'Calendar Events',   key: 'calendarEvents',   local: null, remote: null, status: 'loading' },
  ];

  /** Maps each key to its Dexie query so we can run them individually */
  private readonly localQueries: Record<string, LocalCountFn> = {
    boundBooks:       (cid) => this.localDb.boundBooks.where('company_id').equals(cid).count(),
    firearms:         (cid) => this.localDb.firearms.where('company_id').equals(cid).count(),
    ffls:             (cid) => this.localDb.ffls.where('company_id').equals(cid).count(),
    vendors:          (cid) => this.localDb.vendors.where('company_id').equals(cid).count(),
    customers:        (cid) => this.localDb.customers.where('company_id').equals(cid).count(),
    productsServices: (cid) => this.localDb.productsServices.where('company_id').equals(cid).count(),
    salesReceipts:    (cid) => this.localDb.salesReceipts.where('company_id').equals(cid).count(),
    expenseRecords:   (cid) => this.localDb.expenseRecords.where('company_id').equals(cid).count(),
    form4473s:        (cid) => this.localDb.form4473s.where('company_id').equals(cid).count(),
    reservedFirearms: (cid) => this.localDb.reservedFirearms.where('company_id').equals(cid).count(),
    reserved4473s:    (cid) => this.localDb.reserved4473s.where('company_id').equals(cid).count(),
    calendarEvents:   (cid) => this.localDb.calendarEvents.where('company_id').equals(cid).count(),
  };

  get pendingCount(): number {
    return this.collections.filter(c => c.status === 'loading' || c.status === 'local-done').length;
  }

  get totalMismatches(): number {
    return this.collections.filter(c => c.status === 'mismatch').length;
  }

  get overallStatus(): 'loading' | 'ok' | 'mismatch' | 'error' | 'syncing' {
    if (this.isLoading) return 'loading';
    if (this.isSyncing) return 'syncing';
    if (this.hasError) return 'error';
    if (this.totalMismatches > 0) return 'mismatch';
    return 'ok';
  }

  constructor(
    public modalRef: BsModalRef,
    private http: HttpClient,
    private sharedService: SharedService,
    private localDb: LocalDatabaseService,
    private accountingService: AccountingService,
    private calendarService: CalendarService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
  ) {}

  async ngOnInit(): Promise<void> {
    this.companyId = this.sharedService.company_info?.company_id || '';
    await this.runCheck();
  }

  async runCheck(): Promise<void> {
    this.isLoading = true;
    this.hasError = false;
    // Reset all rows back to loading state
    this.collections.forEach(c => {
      c.status = 'loading';
      c.local = null;
      c.remote = null;
    });
    this.cdr.detectChanges();

    try {
      // Fire off all local IndexedDB queries individually — each one
      // updates its own row as it resolves so the user sees progress.
      const localPromises = this.collections.map(col =>
        this.localQueries[col.key](this.companyId)
          .then(count => {
            this.ngZone.run(() => {
              col.local = count;
              col.status = 'local-done';
              this.cdr.detectChanges();
            });
          })
          .catch(() => {
            this.ngZone.run(() => {
              col.local = 0;
              col.status = 'local-done';
              this.cdr.detectChanges();
            });
          })
      );

      // Fire off the single remote call in parallel
      const remotePromise = this.getRemoteCounts();

      // Wait for all local queries first (they update individually above)
      await Promise.all(localPromises);

      // Now wait for the remote result and resolve each row
      const remoteCounts = await remotePromise;

      let needsSync = false;
      this.ngZone.run(() => {
        for (const col of this.collections) {
          col.remote = remoteCounts[col.key] ?? null;
          if (col.remote === null) {
            col.status = 'error';
          } else {
            col.status = col.local === col.remote ? 'ok' : 'mismatch';
            if (col.status === 'mismatch') needsSync = true;
          }
        }
        this.lastChecked = new Date();
        this.isLoading = false;
        this.cdr.detectChanges();
      });

      if (needsSync) {
        await this.autoSyncMismatches();
      }
    } catch (err) {
      console.error('[SyncCheck] failed:', err);
      this.ngZone.run(() => {
        this.hasError = true;
        this.collections.forEach(c => { if (c.status === 'loading' || c.status === 'local-done') c.status = 'error'; });
        this.isLoading = false;
        this.cdr.detectChanges();
      });
    }
  }

  private async getRemoteCounts(): Promise<Record<string, number | null>> {
    const response = await firstValueFrom(
      this.http.get<{ company_id: string; counts: Record<string, number | null> }>(
        `${this.sharedService.apiUrl}/sync_check/${this.companyId}`,
      ),
    );
    return response.counts;
  }

  private async autoSyncMismatches(): Promise<void> {
    this.ngZone.run(() => {
      this.isSyncing = true;
      this.cdr.detectChanges();
    });

    const syncFns: Record<string, () => Promise<any>> = {
      boundBooks:       () => this.sharedService.getBoundBookEntries(),
      firearms:         () => this.sharedService.getFirearmList(),
      ffls:             () => this.sharedService.getFFLList(),
      vendors:          () => this.accountingService.getVendors(),
      customers:        () => this.accountingService.getCustomers(),
      productsServices: () => this.accountingService.getProductsServices(),
      salesReceipts:    () => this.accountingService.getSalesReceipts(),
      expenseRecords:   () => this.accountingService.getExpenseRecords(),
      form4473s:        () => this.sharedService.getForm4473ByCompanyId(this.companyId),
      reservedFirearms: () => this.sharedService.getReservedFirearmList(),
      reserved4473s:    () => this.sharedService.getReserved4473List(),
      calendarEvents:   () => this.calendarService.getEvents(),
    };

    const mismatches = this.collections.filter(c => c.status === 'mismatch');
    for (const col of mismatches) {
      try {
        this.ngZone.run(() => {
          col.status = 'syncing';
          this.cdr.detectChanges();
        });

        const syncFn = syncFns[col.key];
        if (syncFn) {
          await syncFn();
          
          // Re-fetch just the local count for this collection after sync
          const newLocalCount = await this.localQueries[col.key](this.companyId);
          this.ngZone.run(() => {
            col.local = newLocalCount;
            col.status = col.local === col.remote ? 'ok' : 'mismatch';
            this.cdr.detectChanges();
          });
        }
      } catch (err) {
        console.error(`[SyncCheck] Auto-sync failed for ${col.key}:`, err);
        this.ngZone.run(() => {
          col.status = 'error';
          this.cdr.detectChanges();
        });
      }
    }

    this.ngZone.run(() => {
      this.isSyncing = false;
      this.cdr.detectChanges();
    });
  }

  statusIcon(status: CollectionStatus['status']): string {
    switch (status) {
      case 'ok':         return '✓';
      case 'mismatch':   return '✗';
      case 'error':      return '!';
      case 'local-done': return '½';
      case 'syncing':    return '↻';
      default:           return '…';
    }
  }

  statusColor(status: CollectionStatus['status']): string {
    switch (status) {
      case 'ok':         return '#4ade80';
      case 'mismatch':   return '#f87171';
      case 'error':      return '#facc15';
      case 'local-done': return '#60a5fa'; // blue — local done, waiting for remote
      case 'syncing':    return '#a78bfa'; // purple — syncing
      default:           return '#94a3b8'; // grey — waiting
    }
  }
}
